export class ApiError extends Error {
  constructor({ message, status, code, details, payload }) {
    super(message);
    this.name = "ApiError";
    this.status = status; // HTTP status code
    this.code = code; // error code from the server, if any
    this.details = details; // additional error details from the server, if any
    this.payload = payload; // payload from the server, if any
  }
}

export function createApiClient({
  isAuthenticated,
  ensureValidToken,
  getAccessToken,
  getTenantId,
}) {
  return async function apiClient(
    path, // the resource path of the API (e.g., "/me", "/tenants/products")
    {
      requireAuth = true, // by default, authentication is required, unless overridden in options (as a safe fallback)
      requireTenant = true, // by default, tenant selection is required, unless overridden in options (as a safe fallback)
      headers: customHeaders = {}, // extract the headers from options if given
      query = {}, // extract the query params from options if given
      // method = "GET", // default to GET method if not set
      ...options // spread other fetch options
    } = {}
  ) {

    const baseUrl = process.env.REACT_APP_BACKEND_URL;
    if (!baseUrl) {
      throw new Error("REACT_APP_BACKEND_URL is not defined");
    }

    // Test the path is a valid URL path
    if (!path.startsWith("/")) {
      throw new Error(`Invalid API path. Path should start with "/" Given: ${path}`);
    }

    // construct full URL and set query parameters for the request (base URL + path)
    const url = new URL(`${baseUrl}${path}`);
    Object.entries(query).forEach(([key, value]) => {
      url.searchParams.append(key, value);
    });

    // ---- Auth enforcement ----
    if (requireAuth) {
      await ensureValidToken();

      if (!isAuthenticated()) {
        throw new ApiError({
          message: "Not authenticated",
          status: 401,
        });
      }
    }

    const headers = {
      Accept: "application/json",
      ...customHeaders,
    };

    if (requireAuth) {
      headers.Authorization = `Bearer ${getAccessToken()}`;
    }

    // ---- Tenant enforcement ----
    const tenantId = getTenantId?.();
    if (tenantId) {
      headers["tenant-id"] = tenantId;
    } else if (requireTenant) {
      throw new ApiError({
        message: "Tenant not selected",
        status: 400,
      });
    }

    // ---- Content-Type handling ----
    if (options.body && !(options.body instanceof FormData)) {
      headers["Content-Type"] = "application/json";
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });


    // ---- Response handling ----
    // Parse JSON response if applicable (if the response has JSON content-type header with application/json)
    let payload = null;
    const contentType = response.headers.get("content-type");
    if (contentType && contentType.includes("application/json")) {
      try {
        payload = await response.json();
      } catch (e) {
        // non-JSON response
        throw new ApiError({
          message: "Invalid response from server",
          status: response.status,
          details: "Response from server is not valid JSON. Unable to parse. Please check network tab for more details.",
        });
      }
    }

    if (!response.ok) {
      console.error("API Error:", payload);
      throw new ApiError({
        message: payload?.message || "Server communication error",
        status: response.status,
        code: payload?.code,
        details: payload?.details,
        payload: payload,
      });
    }

    return payload;
  };
}